import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Spin, Alert, Empty } from 'antd';
import { LeftOutlined, RightOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import { professorService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import EncontroCard from './EncontroCard';
import '../../styles/PortalProfessor.css';

dayjs.locale('pt-br');

const { Text, Title } = Typography;

const FORMATO_API = 'YYYY-MM-DD';

const MENSAGEM_SEM_VINCULO =
  'Seu usuário ainda não está vinculado a um professor/equipe. Procure a administração.';

const PortalProfessorHome = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(() => dayjs());
  const [encontros, setEncontros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null); // { status, message } | null
  const [avisoGradeIncompleta, setAvisoGradeIncompleta] = useState(false);

  const carregarEncontros = useCallback(async (dataAlvo) => {
    setLoading(true);
    setErro(null);
    setAvisoGradeIncompleta(false);
    const dataFormatada = dataAlvo.format(FORMATO_API);

    // Fase 13: primeiro materializa idempotentemente as aulas previstas
    // pela grade da equipe nesta data, depois lista normalmente.
    try {
      const preparo = await professorService.prepararEncontros(dataFormatada);
      if (Array.isArray(preparo?.data?.turmas_ignoradas) && preparo.data.turmas_ignoradas.length > 0) {
        setAvisoGradeIncompleta(true);
      }
    } catch (err) {
      const status = err?.response?.status;
      // 400 = data fora da janela de preparo (ano civil corrente até
      // hoje + 7 dias): não é uma falha real, apenas não há nada novo a
      // materializar para essa data — a listagem abaixo segue normal e
      // continua exibindo encontros já existentes fora da janela (o
      // histórico nunca é escondido). Qualquer outro erro (sessão
      // expirada, sem permissão, falha técnica) segue o tratamento normal.
      if (status !== 400) {
        if (status === 403) {
          setErro({ status, message: MENSAGEM_SEM_VINCULO });
        } else {
          const { message: mensagem } = buildApiErrorMessage(
            err,
            'Não foi possível preparar suas aulas. Tente novamente.',
            { context: status === 401 ? 'session' : undefined }
          );
          setErro({ status, message: mensagem });
        }
        setEncontros([]);
        setLoading(false);
        return;
      }
    }

    try {
      const response = await professorService.listarEncontros(dataFormatada);
      setEncontros(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setErro({ status, message: MENSAGEM_SEM_VINCULO });
      } else {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível carregar suas aulas. Tente novamente.',
          { context: status === 401 ? 'session' : undefined }
        );
        setErro({ status, message: mensagem });
      }
      setEncontros([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarEncontros(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const irParaDiaAnterior = () => setData((atual) => atual.subtract(1, 'day'));
  const irParaHoje = () => setData(dayjs());
  const irParaProximoDia = () => setData((atual) => atual.add(1, 'day'));
  const tentarNovamente = () => carregarEncontros(data);

  const abrirChamada = (encontro) => {
    navigate(`/professor/encontros/${encontro.id}/chamada`);
  };

  const ehHoje = data.isSame(dayjs(), 'day');

  const renderConteudo = () => {
    if (loading) {
      return (
        <div className="portal-professor__state" role="status" aria-live="polite">
          <Spin size="large" />
          <Text type="secondary">Carregando suas aulas...</Text>
        </div>
      );
    }

    if (erro) {
      // 401: o interceptor global de api.js já tenta refresh; se persistir,
      // a proxima navegacao autenticada cai em PrivateRoute -> /login.
      // Aqui so evitamos tela branca e oferecemos retry manual.
      return (
        <div className="portal-professor__state">
          <Alert
            type={erro.status === 403 ? 'warning' : 'error'}
            showIcon
            message={erro.status === 403 ? 'Acesso indisponível' : 'Não foi possível carregar'}
            description={erro.message}
            style={{ maxWidth: 480, textAlign: 'left' }}
          />
          {erro.status !== 403 && (
            <Button icon={<ReloadOutlined />} onClick={tentarNovamente}>
              Tentar novamente
            </Button>
          )}
        </div>
      );
    }

    if (encontros.length === 0) {
      return (
        <div className="portal-professor__state">
          <Empty description="Nenhuma aula encontrada para esta data." />
        </div>
      );
    }

    return (
      <div className="portal-professor__list">
        {encontros.map((encontro) => (
          <EncontroCard key={encontro.id} encontro={encontro} onAbrirChamada={abrirChamada} />
        ))}
      </div>
    );
  };

  return (
    <div className="portal-professor">
      <div className="portal-professor__header">
        <div>
          <Title level={3} className="portal-professor__title">
            Suas aulas
          </Title>
          <Text type="secondary" className="portal-professor__subtitle">
            {data.format('dddd, D [de] MMMM')}
            {ehHoje ? ' (hoje)' : ''}
          </Text>
        </div>
      </div>

      <nav className="portal-professor__date-nav" aria-label="Navegação por data">
        <Button icon={<LeftOutlined />} onClick={irParaDiaAnterior} aria-label="Ver dia anterior">
          Anterior
        </Button>
        <Button onClick={irParaHoje} aria-label="Ver aulas de hoje" type={ehHoje ? 'primary' : 'default'}>
          Hoje
        </Button>
        <Button onClick={irParaProximoDia} aria-label="Ver próximo dia">
          Próximo
          <RightOutlined />
        </Button>
      </nav>

      {avisoGradeIncompleta && !loading && !erro && (
        <Alert
          type="warning"
          showIcon
          banner
          message="Uma ou mais aulas não puderam ser carregadas porque a grade precisa ser revisada."
          className="portal-professor__aviso-grade-incompleta"
        />
      )}

      {renderConteudo()}
    </div>
  );
};

export default PortalProfessorHome;
